import { TFunction } from 'i18next';
import * as Yup from 'yup';
import _get from 'lodash/get';
import classNames from 'classnames';
import {
  SsoIntegrationProviderPresetEnum,
  SsoMappingField,
} from 'graphql-common';
import {
  FieldChangeCallBackArgs,
  FieldItem,
  Value,
  Values,
} from '@lib/interfaces/form';
import Input from '@lib/components/Input/Input';
import { InputTypes } from '@lib/components/Input/enums';
import Select, { SelectComponents } from '@lib/components/Select/Select';
import { GetLoadOptions } from '@lib/components/Select/types';
import SsoPresetOption from '@lib/components/Select/components/SsoPresetOption';
import SsoPresetSingleValue from '@lib/components/Select/components/SsoPresetSingleValue';
import CheckBoxSingleBool from '@lib/components/CheckBoxSingleBool/CheckBoxSingleBool';
import CheckBoxAsToggleCard from '@lib/components/CheckBoxAsToggleCard';
import { TabBarField } from '@lib/components/TabBar';
import ImageUploader, {
  ImageUploaderMode,
} from '@lib/components/ImageUploader/ImageUploader';
import {
  FileSizeLimits,
  UppySsoIconAllowedFileTypes,
} from '@lib/enums/mimeTypes';
import {
  getDefaultNumberValidation,
  getFieldValidation,
  getOptionValidation,
  getOptionsArrayValidation,
} from '@lib/utils/validationUtils';
import OptionsAddItemComponent from '@lib/components/ReactHookForm/OptionsAddItemComponent';
import MultiSelectValueContainer from '@lib/components/Select/components/MultiSelectValueContainer';
import formStyles from '@lib/assets/styles/Form.module.scss';
import { MAX_TEXT_EDITOR_FIELD_LENGTH } from '@lib/enums/form';
import CompanyDomainChipListField from '@lib/components/CompanyDomainChipListField/CompanyDomainChipListField';
import FormSectionHeader from '../components/FormSectionHeader';
import RegenerateCodeButton from '../components/RegenerateCodeButton';
import AttributeMappingRowWrapper from '../components/AttributeMappingRowWrapper';
import SpConfigurationSection from '../components/SpConfigurationSection';
import ReadFromXmlButton from '../components/ReadFromXmlButton';
import {
  AttributeMappingFieldNames,
  SsoConfigTab,
  SsoIntegrationFieldNames,
} from './constants';
import isCustomPreset from './isCustomPreset';
import isTab from './isTab';
import getProviderPresetOptions from './getProviderPresetOptions';
import getConfigTabOptions from './getConfigTabOptions';

type FormFieldsArgs = {
  t: TFunction<'translation', undefined>;
  isEdit?: boolean;
  companyDomainIds?: string[];
  domainCacheKey?: number;
  getCompanyDomainsLoadOptions: GetLoadOptions;
  supportedMappingFields: SsoMappingField[];
  onReadXml?: (content: string) => void;
  onPresetChange?: (preset: string) => void;
  openAddDomainModal?: () => void;
  onRegenerateCode?: () => void;
  isRegeneratingCode?: boolean;
};

export default function getSsoIntegrationFormFields({
  t,
  isEdit,
  companyDomainIds,
  domainCacheKey,
  getCompanyDomainsLoadOptions,
  supportedMappingFields,
  onReadXml,
  onPresetChange,
  openAddDomainModal,
  onRegenerateCode,
  isRegeneratingCode,
}: FormFieldsArgs): FieldItem[] {
  return [
    // === Connection details section ===
    {
      name: SsoIntegrationFieldNames.ConnectionDetailsHeader,
      customRender: () => <FormSectionHeader title={t('connection-details')} />,
      formItemClassName: formStyles.formColumnFullWidth,
    },
    {
      name: SsoIntegrationFieldNames.ProviderPreset,
      label: t('sso-preset'),
      element: Select,
      validation: getOptionValidation({ required: true, t }),
      componentProps: {
        options: getProviderPresetOptions(t),
        isClearable: false,
        isSearchable: false,
        components: {
          Option: SsoPresetOption,
          SingleValue: SsoPresetSingleValue,
        },
      },
      isUseWatch: true,
      isDisabledFn: () => !!isEdit,
      fieldChangeCallBack: ({
        values,
        type,
        name,
      }: FieldChangeCallBackArgs) => {
        if (
          type === 'change' &&
          name === SsoIntegrationFieldNames.ProviderPreset &&
          onPresetChange
        ) {
          const preset = _get(values, [
            SsoIntegrationFieldNames.ProviderPreset,
            'value',
          ]) as string;
          if (preset) {
            setTimeout(() => onPresetChange(preset), 0);
          }
        }
      },
    },
    {
      name: SsoIntegrationFieldNames.Name,
      label: t('sso-name'),
      element: Input,
      validation: getFieldValidation({ t }),
    },
    {
      name: SsoIntegrationFieldNames.IconAttached,
      element: ImageUploader,
      validation: Yup.mixed()
        .nullable()
        .when(SsoIntegrationFieldNames.ProviderPreset, {
          is: (preset: { value: string }) =>
            preset?.value === SsoIntegrationProviderPresetEnum.Custom,
          then: (schema) => schema.required(t('required-field-error')),
          otherwise: (schema) => schema.nullable(),
        }),
      componentProps: {
        label: t('icon'),
        mode: ImageUploaderMode.Compact,
        showOfflineMessage: true,
        allowedFileTypes: UppySsoIconAllowedFileTypes,
        maxFileSize: FileSizeLimits.Image * 1024 * 1024,
        maxImageDimensions: { width: 512, height: 512 },
        requireSquare: true,
        note: t('sso-icon-upload-validation-msg'),
        uploadButtonProps: {
          buttonText: t('upload-image'),
          leftIcon: 'upload',
          fullWidth: true,
        },
        useReuploadButton: true,
        showDeleteAllButton: true,
      },
      isVisibleFn: isCustomPreset,
    },
    {
      name: SsoIntegrationFieldNames.CompanyDomainIds,
      label: t('domains'),
      element: Select,
      validation: getOptionsArrayValidation({ t, required: true }),
      componentProps: {
        defaultOptions: false,
        isAsync: true,
        isMulti: true,
        getLoadOptions: getCompanyDomainsLoadOptions,
        cacheUniqs: [domainCacheKey],
        maxMenuHeight: 200,
        usePortal: true,
        createNewOptionProps: openAddDomainModal
          ? {
              title: t('add-domain'),
              onCLick: openAddDomainModal,
            }
          : undefined,
        components: {
          ValueContainer: MultiSelectValueContainer,
        } as unknown as SelectComponents,
      },
      formItemClassName: formStyles.formColumnFullWidth,
      hideErrorsSpace: true,
    },
    {
      name: SsoIntegrationFieldNames.CompanyDomainIdsList,
      element: CompanyDomainChipListField,
      componentProps: {
        fieldName: SsoIntegrationFieldNames.CompanyDomainIds,
        ids: companyDomainIds,
      },
      isUseWatch: true,
      formItemClassName: formStyles.formColumnFullWidth,
    },

    // === SSO configuration section ===
    {
      name: SsoIntegrationFieldNames.SsoConfigurationHeader,
      customRender: () => <FormSectionHeader title={t('sso-configuration')} />,
      formItemClassName: classNames(
        formStyles.formColumnFullWidth,
        formStyles.blockStart,
      ),
    },
    {
      name: SsoIntegrationFieldNames.Code,
      label: t('sso-code'),
      element: Input,
      validation: getFieldValidation({ t }),
      componentProps: {
        disabled: true,
        copyable: true,
      },
      description: t('sso-code-helper'),
      isOptionalFn: () => false,
      formItemClassName: formStyles.formColumnFill,
    },
    {
      name: SsoIntegrationFieldNames.RegenerateCodeButton,
      customRender: () => (
        <RegenerateCodeButton
          onClick={onRegenerateCode}
          isLoading={isRegeneratingCode}
        />
      ),
      formItemClassName: formStyles.formColumnAuto,
      isVisibleFn: () => !isEdit,
    },
    {
      name: SsoIntegrationFieldNames.IdpMetadata,
      label: t('metadata'),
      element: Input,
      validation: getFieldValidation({
        t,
        required: true,
        maxLength: MAX_TEXT_EDITOR_FIELD_LENGTH,
      }),
      componentProps: {
        type: InputTypes.textarea,
        rows: 6,
      },
      isOptionalFn: () => false,
      formItemClassName: formStyles.formColumnFullWidth,
    },
    {
      name: SsoIntegrationFieldNames.ReadFromXmlButton,
      customRender: () => (
        <ReadFromXmlButton
          onFileRead={(content) => {
            if (onReadXml) onReadXml(content);
          }}
        />
      ),
      formItemClassName: formStyles.formColumnFullWidth,
    },

    // === Config tab selector ===
    {
      name: SsoIntegrationFieldNames.ConfigTab,
      element: TabBarField,
      componentProps: {
        items: getConfigTabOptions(t),
      },
      isUseWatch: true,
      formItemClassName: formStyles.formColumnFullWidth,
      hideErrorsSpace: true,
    },

    // === Attributes tab ===
    {
      name: SsoIntegrationFieldNames.AttributeMapping,
      subFieldsEntity: 'mapping',
      subFieldsAddMoreButtonText: t('add-mapping'),
      subFieldsMinLength: 1,
      isSubItemRemovableFn: (value: Value) => {
        return !(value as { isDefault?: boolean })?.isDefault;
      },
      fieldComponents: {
        FieldArrayItemWrapperComponent: AttributeMappingRowWrapper,
        FieldArrayAddItemComponent: OptionsAddItemComponent,
        FieldArrayEmptyStateComponent: OptionsAddItemComponent,
      },
      subFields: [
        {
          name: AttributeMappingFieldNames.Attribute,
          label: t('attribute'),
          element: Input,
          formItemClassName: formStyles.formColumn,
          hideErrorsSpace: true,
        },
        {
          name: AttributeMappingFieldNames.MapsTo,
          label: t('maps-to'),
          element: Select,
          componentProps: {
            isSearchable: false,
          },
          getComponentProps: (values: Values, fieldName?: string) => {
            const rowPath = fieldName
              ? fieldName.replace(`.${AttributeMappingFieldNames.MapsTo}`, '')
              : '';
            const match = rowPath.match(/\.(\d+)$/);
            const rowIndex = match ? parseInt(match[1], 10) : -1;
            const allRows = _get(
              values,
              SsoIntegrationFieldNames.AttributeMapping,
              [],
            ) as Array<{ mapsTo?: { value?: string } }>;
            const alreadyUsed = new Set(
              allRows
                .filter((_, idx) => idx !== rowIndex)
                .map((row) => row?.mapsTo?.value)
                .filter((v): v is string => !!v),
            );
            return {
              options: supportedMappingFields.map((f) => ({
                value: f.key,
                label: f.key,
                isDisabled: alreadyUsed.has(f.key),
              })),
            };
          },
          isDisabledFn: (values: Values, name?: string) => {
            if (!name) return false;
            const rowPath = name.replace(
              `.${AttributeMappingFieldNames.MapsTo}`,
              '',
            );
            if (!isEdit) {
              return !!_get(values, `${rowPath}.isDefault`);
            }
            const mapsToValue = _get(
              values,
              `${rowPath}.${AttributeMappingFieldNames.MapsTo}.value`,
            ) as string | undefined;
            return !!supportedMappingFields.find(
              (f) => f.key === mapsToValue && f.required,
            );
          },
          formItemClassName: formStyles.formColumn,
          hideErrorsSpace: true,
        },
      ],
      isVisibleFn: isTab(SsoConfigTab.Attributes),
      formItemClassName: formStyles.formColumnFullWidth,
    },

    // === Provisioning tab ===
    {
      name: SsoIntegrationFieldNames.JitProvisioningEnabled,
      element: CheckBoxSingleBool,
      componentProps: {
        CheckBoxComponent: CheckBoxAsToggleCard,
        options: [{ label: 'checked', value: 'checked' }],
        label: t('enable-jit-provisioning'),
        description: t('enable-jit-provisioning-description'),
      },
      isVisibleFn: isTab(SsoConfigTab.Provisioning),
      formItemClassName: formStyles.formColumnFullWidth,
    },

    // === Advanced tab ===
    {
      name: SsoIntegrationFieldNames.TimeToleranceSeconds,
      label: t('clock-skew-tolerance'),
      element: Input,
      validation: getDefaultNumberValidation({ t, required: true }),
      componentProps: {
        type: InputTypes.number,
        min: 1,
      },
      isVisibleFn: isTab(SsoConfigTab.Advanced),
    },
    {
      name: SsoIntegrationFieldNames.SyncUserAttributes,
      element: CheckBoxSingleBool,
      componentProps: {
        CheckBoxComponent: CheckBoxAsToggleCard,
        options: [{ label: 'checked', value: 'checked' }],
        label: t('sync-user-profile-on-login'),
        description: t('sync-user-profile-tooltip'),
      },
      isVisibleFn: isTab(SsoConfigTab.Advanced),
      formItemClassName: formStyles.formColumnFullWidth,
    },
    {
      name: SsoIntegrationFieldNames.AllowIdpInitiated,
      element: CheckBoxSingleBool,
      componentProps: {
        CheckBoxComponent: CheckBoxAsToggleCard,
        options: [{ label: 'checked', value: 'checked' }],
        label: t('allow-idp-initiated'),
        description: t('allow-idp-initiated-description'),
      },
      isVisibleFn: isTab(SsoConfigTab.Advanced),
      formItemClassName: formStyles.formColumnFullWidth,
    },

    // === SP Configuration section ===
    {
      name: SsoIntegrationFieldNames.SpConfiguration,
      customRender: (values: Values) => {
        const code = _get(values, SsoIntegrationFieldNames.Code, '') as string;
        return <SpConfigurationSection code={code} />;
      },
      isUseWatch: true,
      formItemClassName: classNames(
        formStyles.formColumnFullWidth,
        formStyles.blockStart,
      ),
    },
  ];
}
